import Base from '@studiometa/js-toolkit';
import useResize from '@studiometa/js-toolkit/services/resize';
import Accordion from '@studiometa/js-toolkit/components/Accordion';
import Modal from '@studiometa/js-toolkit/components/Modal';
import AccordionFooter from './components/AccordionFooter.js';
import Header from './components/Header.js';
import Slider from './components/Slider.js';
import YoutubePlayer from './components/YoutubePlayer.js';
import HeroSlider from './components/HeroSlider.js';
import Product from './components/Product.js';
import Filters from './components/Filters.js';
import Scroll from './components/Scroll.js';
import Loader from './components/Loader.js';
import { isDev } from './config.js';
import useHighway from './utils/highway/index.js';
import CanvasHome from './components/CanvasHome.js';

/**
 * Main App class.
 */
class App extends Base {
  /**
   * App config.
   * @returns {object}
   */
  get config() {
    return {
      log: isDev(),
      name: 'App',
      components: {
        Header,
        AccordionFooter,
        Accordion,
        Slider,
        Modal,
        YoutubePlayer,
        HeroSlider,
        Product,
        Scroll,
        Loader,
        CanvasHome,
        KramerStoreLocator: () =>
          import(
            /* webpackChunkName: "KramerStoreLocator" */ './components/KramerStoreLocator/index.js'
          ),
        Filters,
      },
    };
  }

  /**
   * Loader instance
   */
  get loaderInstance() {
    return this.$children.Loader[0];
  }

  /**
   * Get current breakpoint
   */
  get breakpoint() {
    return useResize().props().breakpoint;
  }

  /**
   * Mounted hook
   */
  mounted() {
    this.highway = useHighway(this);

    this.hasScrolled = false;
    this.testScroll(window.pageYOffset);
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);

    // @note Because of HighWayJS, breakpoint should be inside data-router-view
    // But breakpoint are outside in the front end, so we move breadcrumb on load, on and page change
    this.$refs.breadcrumbContainer.innerHTML = this.$refs.breadcrumb.innerHTML;

    this.checkMenuLinks();
  }

  /**
   * Resized hook
   */
  resized() {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
  }

  /**
   * Remove openSubmenu links from the HighWayJS configuration to not trigger a page transition when opening submenu.
   * @see https://highway.js.org/api.html
   */
  checkMenuLinks() {
    if (['xxs', 'xs', 's'].includes(this.breakpoint)) {
      this.highway.detach(this.$children.Header[0].$refs.openSubmenu);
    }
  }

  /**
   * Service scrolled hook
   * @see https://js-toolkit.meta.fr/services/scroll.html
   * @param {number} x The current horizontal scroll position.
   * @param {number} y The current vertical scroll position.
   * @param {object} changed For both axis, whether or not the current position has changed since the last one.
   * @param {object} last The last position on both the x and y axis.
   * @param {object} delta The difference between the last position and the current one.
   * @param {object} progress The scroll position on both axis mapped to a 0 to 1 range, based on the max property.
   * @param {object} max The maximum value the scroll can reach.
   */
  scrolled({ y }) {
    this.testScroll(y);
  }

  /**
   * Test the scroll.
   * @param {number} y The scroll y position.
   */
  testScroll(y) {
    if (y > 50 && !this.hasScrolled) {
      this.hasScrolled = true;
      this.$el.classList.add('has-scrolled');
    }

    if (y < 50 && this.hasScrolled) {
      this.hasScrolled = false;
      this.$el.classList.remove('has-scrolled');
    }
  }
}

const app = new App(document.documentElement);
window.app = app;
export default app;
